import {
  APP_BACK_ROUTE_POLICY,
  normalizeAppBackPath,
  resolveAppBackRoute,
} from '@/constants/appBackRoutePolicy';

const createSessionId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

  return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const normalizeDepth = (depth, fallback = 1) => {
  const value = Number(depth);
  if (!Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(value));
};

const getDefaultHistorySnapshot = () => ({
  state: typeof window === 'undefined' ? null : window.history.state,
  origin: typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
});

export const isHardwareBackPayload = (payload) =>
  !!payload &&
  typeof payload === 'object' &&
  typeof payload.sessionId === 'string' &&
  payload.sessionId.length > 0 &&
  typeof payload.requestId === 'string' &&
  payload.requestId.length > 0;

export const createAppBackCoordinator = ({
  sessionIdFactory = createSessionId,
  getHistorySnapshot = getDefaultHistorySnapshot,
  base = '/',
} = {}) => {
  let sessionId = sessionIdFactory();
  let router = null;
  let historyWatermark = getHistorySnapshot()?.state?.position;
  if (!Number.isInteger(historyWatermark)) historyWatermark = 0;

  let sequence = 0;
  let rootGeneration = 0;
  let currentRequest = null;
  let lastCompletedRequest = null;
  let navigationSettler = null;
  let lastRoutePath = null;

  const layers = new Map();
  const reducers = new Map();

  const markBackStateChanged = () => {
    rootGeneration += 1;
  };

  const getRootToken = () => `root-${rootGeneration.toString(36)}`;

  const getRoutePath = () => normalizeAppBackPath(router?.currentRoute?.value?.path || '/main');

  const getHistoryContext = () => {
    const snapshot = getHistorySnapshot() || {};
    return {
      historyState: snapshot.state,
      historyWatermark,
      origin: snapshot.origin,
      base,
    };
  };

  const getEligibleLayers = () =>
    [...layers.values()].filter((entry) => entry.open).sort((a, b) => b.sequence - a.sequence);

  const isReducerInRoute = (entry, routePath) => {
    if (!entry.route) return true;
    const scope = typeof entry.route === 'function' ? entry.route() : entry.route;
    if (Array.isArray(scope)) return scope.includes(routePath);
    return !scope || scope === routePath;
  };

  const getEligibleReducers = () => {
    const routePath = getRoutePath();
    return [...reducers.values()]
      .filter((entry) => entry.active && isReducerInRoute(entry, routePath))
      .sort((a, b) => b.sequence - a.sequence);
  };

  const readReducerDepth = (entry) => {
    if (!entry.active) return 0;
    if (!entry.getDepth) return 1;
    try {
      return normalizeDepth(entry.getDepth(), 1);
    } catch {
      return 1;
    }
  };

  const createResult = ({ requestId, outcome, kind, reason }) => ({
    sessionId,
    requestId: requestId ?? null,
    outcome,
    ...(kind ? { kind } : {}),
    ...(outcome === 'root' ? { rootToken: getRootToken() } : {}),
    ...(reason ? { reason } : {}),
  });

  const performLayerBack = async (entry, requestId) => {
    entry.closing = true;
    if (!entry.dismissible) {
      entry.closing = false;
      return createResult({ requestId, outcome: 'handled', kind: 'layer' });
    }

    try {
      await entry.closeFromBack?.();
      entry.closing = false;
      return createResult({ requestId, outcome: 'handled', kind: 'layer' });
    } catch {
      entry.closing = false;
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: 'layer-error',
      });
    }
  };

  const performReducerBack = async (entry, requestId) => {
    try {
      await entry.unwindOne();
      return createResult({ requestId, outcome: 'handled', kind: 'local' });
    } catch {
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: 'reducer-error',
      });
    }
  };

  const navigateWithReplace = async (target, requestId) => {
    try {
      const failure = await router.replace(target);
      if (failure) {
        return createResult({
          requestId,
          outcome: 'rejected',
          reason: 'navigation-failed',
        });
      }
      return createResult({ requestId, outcome: 'handled', kind: 'route' });
    } catch {
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: 'navigation-error',
      });
    }
  };

  const navigateWithHistory = async (requestId) => {
    const settled = new Promise((resolve) => {
      navigationSettler = resolve;
    });

    try {
      router.back();
    } catch {
      navigationSettler = null;
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: 'navigation-error',
      });
    }

    const outcome = await settled;
    if (outcome?.failure || outcome?.error) {
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: outcome.error ? 'navigation-error' : 'navigation-failed',
      });
    }

    return createResult({ requestId, outcome: 'handled', kind: 'route' });
  };

  const performOneBackCore = async ({ requestId = null } = {}) => {
    const layer = getEligibleLayers().find((entry) => !entry.closing);
    if (layer) return performLayerBack(layer, requestId);

    const reducer = getEligibleReducers()[0];
    if (reducer) return performReducerBack(reducer, requestId);

    if (!router) {
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: 'router-not-ready',
      });
    }

    const route = getRoutePath();
    const { policy, target } = resolveAppBackRoute(route, getHistoryContext());
    if (policy === APP_BACK_ROUTE_POLICY.ROOT) {
      return createResult({ requestId, outcome: 'root' });
    }
    if (policy === APP_BACK_ROUTE_POLICY.UNSUPPORTED) {
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: 'unsupported-route',
      });
    }
    if (policy === APP_BACK_ROUTE_POLICY.HISTORY) return navigateWithHistory(requestId);
    return navigateWithReplace(target || '/main', requestId);
  };

  const handleHardwareBack = async (payload) => {
    const requestId = typeof payload?.requestId === 'string' ? payload.requestId : null;

    if (!isHardwareBackPayload(payload)) {
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: 'invalid-payload',
      });
    }
    if (payload.sessionId !== sessionId) {
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: 'session-mismatch',
      });
    }
    if (lastCompletedRequest?.requestId === requestId) return lastCompletedRequest.result;
    if (currentRequest?.requestId === requestId) return currentRequest.promise;
    if (currentRequest) {
      return createResult({
        requestId,
        outcome: 'rejected',
        reason: 'busy',
      });
    }

    const promise = performOneBackCore({ requestId });
    currentRequest = { requestId, promise };
    try {
      const result = await promise;
      lastCompletedRequest = { requestId, result };
      return result;
    } finally {
      if (currentRequest?.requestId === requestId) currentRequest = null;
    }
  };

  const performOneBack = async () => {
    if (currentRequest) {
      return createResult({
        requestId: null,
        outcome: 'rejected',
        reason: 'busy',
      });
    }

    const promise = performOneBackCore();
    currentRequest = { requestId: null, promise };
    try {
      return await promise;
    } finally {
      if (currentRequest?.promise === promise) currentRequest = null;
    }
  };

  const registerLayer = ({ id, dismissible = true, closeFromBack }) => {
    if (!id || layers.has(id)) throw new Error(`Duplicate app-back layer id: ${id}`);
    if (dismissible && typeof closeFromBack !== 'function') {
      throw new Error(`Missing closeFromBack for app-back layer: ${id}`);
    }

    const entry = {
      id,
      dismissible,
      closeFromBack,
      open: false,
      closing: false,
      sequence: 0,
    };
    layers.set(id, entry);

    return {
      setOpen(open) {
        const nextOpen = !!open;
        if (entry.open === nextOpen) return;
        entry.open = nextOpen;
        entry.closing = false;
        if (nextOpen) entry.sequence = ++sequence;
        markBackStateChanged();
      },
      unregister() {
        if (!layers.delete(id)) return;
        if (entry.open) markBackStateChanged();
      },
    };
  };

  const registerReducer = ({ id, unwindOne, getDepth, route }) => {
    if (!id || reducers.has(id)) throw new Error(`Duplicate app-back reducer id: ${id}`);
    if (typeof unwindOne !== 'function') throw new Error(`Missing unwindOne for reducer: ${id}`);

    const entry = {
      id,
      unwindOne,
      getDepth,
      route,
      active: false,
      depth: 0,
      sequence: 0,
    };
    reducers.set(id, entry);

    const updateState = (active = entry.active) => {
      const nextActive = !!active;
      const previousActive = entry.active;
      const previousDepth = entry.depth;
      entry.active = nextActive;
      entry.depth = readReducerDepth(entry);
      if (nextActive && !previousActive) entry.sequence = ++sequence;
      if (previousActive !== nextActive || previousDepth !== entry.depth) markBackStateChanged();
    };

    return {
      setActive(active) {
        updateState(active);
      },
      notifyDepthChange() {
        updateState();
      },
      unregister() {
        if (!reducers.delete(id)) return;
        if (entry.active) markBackStateChanged();
      },
    };
  };

  const attachRouter = (nextRouter) => {
    const previousPath = lastRoutePath;
    router = nextRouter;
    lastRoutePath = getRoutePath();
    const position = getHistorySnapshot()?.state?.position;
    if (Number.isInteger(position)) historyWatermark = position;
    if (previousPath && previousPath !== lastRoutePath) markBackStateChanged();
  };

  const notifyNavigationSettled = (to, from, failure) => {
    const nextPath = normalizeAppBackPath(to?.path || getRoutePath());
    const previousPath = normalizeAppBackPath(from?.path || lastRoutePath || nextPath);
    if (!failure && nextPath !== previousPath) markBackStateChanged();
    if (!failure) lastRoutePath = nextPath;

    const settle = navigationSettler;
    navigationSettler = null;
    settle?.({ to, from, failure });
  };

  const resetForTests = ({ nextSessionIdFactory = sessionIdFactory } = {}) => {
    layers.clear();
    reducers.clear();
    sequence = 0;
    rootGeneration = 0;
    currentRequest = null;
    lastCompletedRequest = null;
    navigationSettler = null;
    lastRoutePath = null;
    router = null;
    sessionId = nextSessionIdFactory();
    const position = getHistorySnapshot()?.state?.position;
    historyWatermark = Number.isInteger(position) ? position : 0;
  };

  return {
    attachRouter,
    getRootToken,
    getSessionId: () => sessionId,
    handleHardwareBack,
    notifyNavigationSettled,
    performOneBack,
    registerLayer,
    registerReducer,
    resetForTests,
  };
};

export const appBackCoordinator = createAppBackCoordinator();
